import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["textarea"];

  bold()        { this.#wrap("**", "**", "bold text") }
  italic()      { this.#wrap("*", "*", "italic text") }
  strike()      { this.#wrap("~~", "~~", "strikethrough text") }
  inlineCode()  { this.#wrap("`", "`", "code") }

  link() {
    const ta = this.textareaTarget;
    const { selectionStart: start, selectionEnd: end } = ta;
    const selected = ta.value.slice(start, end);
    if (selected) {
      this.#replaceSelection(start, end, `[${selected}](url)`);
      const urlStart = start + 1 + selected.length + 2;
      ta.setSelectionRange(urlStart, urlStart + 3);
    } else {
      this.#replaceSelection(start, end, "[]()");
      ta.setSelectionRange(start + 1, start + 1);
    }
  }
  h1()          { this.#linePrefix("# ") }
  h2()          { this.#linePrefix("## ") }
  h3()          { this.#linePrefix("### ") }
  quote()       { this.#linePrefix("> ") }
  ul()          { this.#linePrefix("- ") }
  ol()          { this.#linePrefix("1. ") }
  hr()          { this.#insertAtCaret("\n\n---\n\n") }

  pasteLink(event) {
    const ta = this.textareaTarget;
    const { selectionStart: start, selectionEnd: end } = ta;
    const selected = ta.value.slice(start, end);
    if (!selected) return;

    const text = event.clipboardData.getData("text/plain").trim();
    if (!text.startsWith("http")) return;

    event.preventDefault();
    const markdown = `[${selected}](${text})`;
    this.#replaceSelection(start, end, markdown);
    ta.setSelectionRange(start + markdown.length, start + markdown.length);
  }

  codeBlock() {
    const ta = this.textareaTarget;
    const { selectionStart: start, selectionEnd: end } = ta;
    const selected = ta.value.slice(start, end);
    const inner = selected || "code here";
    const snippet = "```\n" + inner + "\n```";
    this.#replaceSelection(start, end, snippet);
    if (!selected) {
      ta.setSelectionRange(start + 4, start + 4 + "code here".length);
    }
  }

  // --- private helpers ---

  #wrap(before, after, placeholder) {
    const ta = this.textareaTarget;
    const { selectionStart: start, selectionEnd: end } = ta;
    const selected = ta.value.slice(start, end);

    if (selected) {
      this.#replaceSelection(start, end, before + selected + after);
      ta.setSelectionRange(start + before.length, start + before.length + selected.length);
    } else {
      this.#replaceSelection(start, end, before + placeholder + after);
      ta.setSelectionRange(start + before.length, start + before.length + placeholder.length);
    }
  }

  #linePrefix(prefix) {
    const ta = this.textareaTarget;
    const { selectionStart: start, selectionEnd: end } = ta;
    const val = ta.value;

    // Expand selection to full lines
    const lineStart = val.lastIndexOf("\n", start - 1) + 1;
    const lineEnd = val.indexOf("\n", end);
    const blockEnd = lineEnd === -1 ? val.length : lineEnd;

    const lines = val.slice(lineStart, blockEnd).split("\n");
    const alreadyApplied = lines.every(l => l.startsWith(prefix));

    const transformed = alreadyApplied
      ? lines.map(l => l.slice(prefix.length)).join("\n")
      : lines.map(l => prefix + l).join("\n");

    const delta = transformed.length - (blockEnd - lineStart);
    ta.value = val.slice(0, lineStart) + transformed + val.slice(blockEnd);
    ta.setSelectionRange(start + (alreadyApplied ? -prefix.length : prefix.length),
                         end + delta);
    ta.dispatchEvent(new Event("input"));
    ta.focus();
  }

  #insertAtCaret(text) {
    const ta = this.textareaTarget;
    const { selectionStart: start } = ta;
    this.#replaceSelection(start, start, text);
    ta.setSelectionRange(start + text.length, start + text.length);
  }

  #replaceSelection(start, end, replacement) {
    const ta = this.textareaTarget;
    ta.value = ta.value.slice(0, start) + replacement + ta.value.slice(end);
    ta.dispatchEvent(new Event("input"));
    ta.focus();
  }
}
